import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, MapPin, X, Bookmark, BookmarkCheck, ChevronDown, Check,
  SlidersHorizontal, Phone, Users, Clock, Building2, Globe,
  ExternalLink, Calendar, Briefcase, ArrowLeft, Sparkles,
} from 'lucide-react';
import Navbar from './landing/Navbar';
import Footer from './landing/Footer';
import { DriveCard, FilterSelect } from './WalkinDrives';
import { walkinDriveService } from '../services/walkinDriveService';
import { WALKIN_DRIVES } from '../data/walkinDrives';
import { usePrerenderReady } from '../utils/prerender';
import { useSEO } from '../hooks/useSEO';
import {
  cityLabelFromSlug, categoryLabelFromSlug, locationIntro, categoryIntro,
} from '../data/seoLandings';
import config from '../config';

/* ─── translations ───────────────────────────────────────────── */
const T = {
  en: {
    titleParts:       ['Find your next ', 'role', ', not just a listing.'],
    searchPlaceholder:'Job title, skills, or company…',
    locPlaceholder:   'City or Remote…',
    searchBtn:        'Search Jobs',
    popular:          'Popular:',
    recent:           'Recent:',
    tabs:             { all: 'All Jobs', bc: 'Blue Collar', walkin: 'Walk-in Drives' },
    rolesFound:       n => `${n.toLocaleString('en-IN')} roles found`,
    sponsored:        'Sponsored roles',
    featured:         'Featured',
    hideSponsored:    'Hide sponsored jobs',
    filters:          'Filters',
    showResults:      'Show results',
    clear:            'Clear',
    sort:             'Sort:',
    easyApply:        'Easy Apply',
    callNow:          'Call Now',
    save:             'Save',
    saved:            'Saved',
    verified:         'Verified',
    openings:         'openings',
    shift:            'shift',
    noRoles:          'No roles match this filter.',
    noRolesHint:      'Try clearing a filter or searching a different city.',
    comingSoon:       'Coming Soon',
    bcTitle:          'Blue Collar Jobs',
    bcDesc:           "We're building something great for blue collar workers. Stay tuned — this section is launching soon.",
    signIn:           'Sign in to continue',
    signInDesc:       'Create an account or log in to apply for jobs and save opportunities.',
    login:            'Log In',
    createAccount:    'Create Account',
    continueBrowsing: 'Continue browsing',
    filterLabels:     { exp: 'Experience', sal: 'Salary', type: 'Job Type', posted: 'Posted', relevant: 'Most Relevant' },
    filterOpts: {
      exp:       [['0-1','0–1 yrs'],['1-3','1–3 yrs'],['3-6','3–6 yrs'],['6-10','6–10 yrs'],['10+','10+ yrs']],
      sal:       [['0-10','0–10 LPA'],['10-20','10–20 LPA'],['20-30','20–30 LPA'],['30+','30+ LPA']],
      type:      [['Full-time','Full-time'],['Part-time','Part-time'],['Remote','Remote'],['Hybrid','Hybrid'],['Contract','Contract']],
      posted:    [['today','Today'],['3days','Last 3 days'],['week','Last week']],
      sortAll:   [['relevant','Most Relevant'],['newest','Newest'],['salary','Salary: High to Low']],
      bcJobType: [['Full-time','Full-time'],['Part-time','Part-time'],['Contract','Contract'],['Gig','Gig']],
      bcShift:   [['Day','Day'],['Night','Night'],['Rotational','Rotational'],['Flexible','Flexible']],
      bcSort:    [['relevant','Most Relevant'],['newest','Newest'],['wage','Wage: High to Low'],['nearest','Nearest']],
    },
    walkin: {
      placeholder:  'Search by title, company, venue or skill…',
      allCities:    'All cities',
      allCompanies: 'All companies',
      allExp:       'All experience',
      drivesOf:     (f, total) => `${f} of ${total} drives`,
      clearFilters: 'Clear filters',
      noActive:     'No active walk-in drives right now',
      noActiveDesc: 'Check back soon — new drives are added regularly.',
      noMatch:      'No drives match your filters',
    },
  },
  hi: {
    titleParts:       ['अपना अगला ', 'काम', ' ढूंढें, सिर्फ लिस्टिंग नहीं।'],
    searchPlaceholder:'नौकरी का नाम, कौशल या कंपनी…',
    locPlaceholder:   'शहर या रिमोट…',
    searchBtn:        'नौकरी खोजें',
    popular:          'लोकप्रिय:',
    recent:           'हाल के:',
    tabs:             { all: 'सभी नौकरियां', bc: 'ब्लू कॉलर', walkin: 'वॉक-इन ड्राइव' },
    rolesFound:       n => `${n.toLocaleString('en-IN')} नौकरियां मिलीं`,
    sponsored:        'प्रायोजित नौकरियां',
    featured:         'फीचर्ड',
    hideSponsored:    'प्रायोजित नौकरियां छुपाएं',
    filters:          'फ़िल्टर',
    showResults:      'परिणाम दिखाएं',
    clear:            'हटाएं',
    sort:             'क्रम:',
    easyApply:        'आवेदन करें',
    callNow:          'अभी कॉल करें',
    save:             'सेव करें',
    saved:            'सेव किया',
    verified:         'सत्यापित',
    openings:         'पद उपलब्ध',
    shift:            'शिफ्ट',
    noRoles:          'इस फ़िल्टर से कोई नौकरी नहीं मिली।',
    noRolesHint:      'फ़िल्टर साफ करें या किसी दूसरे शहर में खोजें।',
    comingSoon:       'जल्द आ रहा है',
    bcTitle:          'ब्लू कॉलर नौकरियां',
    bcDesc:           'हम ब्लू कॉलर कर्मचारियों के लिए कुछ बेहतरीन बना रहे हैं। जल्द ही यह सेक्शन लॉन्च होगा।',
    signIn:           'जारी रखने के लिए साइन इन करें',
    signInDesc:       'नौकरियों के लिए आवेदन करने और अवसर सेव करने हेतु लॉग इन करें।',
    login:            'लॉग इन',
    createAccount:    'अकाउंट बनाएं',
    continueBrowsing: 'ब्राउज़ करते रहें',
    filterLabels:     { exp: 'अनुभव', sal: 'वेतन', type: 'नौकरी का प्रकार', posted: 'पोस्ट किया', relevant: 'सबसे प्रासंगिक' },
    filterOpts: {
      exp:       [['0-1','0–1 वर्ष'],['1-3','1–3 वर्ष'],['3-6','3–6 वर्ष'],['6-10','6–10 वर्ष'],['10+','10+ वर्ष']],
      sal:       [['0-10','0–10 एलपीए'],['10-20','10–20 एलपीए'],['20-30','20–30 एलपीए'],['30+','30+ एलपीए']],
      type:      [['Full-time','पूर्णकालिक'],['Part-time','अंशकालिक'],['Remote','रिमोट'],['Hybrid','हाइब्रिड'],['Contract','अनुबंध']],
      posted:    [['today','आज'],['3days','पिछले 3 दिन'],['week','पिछले हफ्ते']],
      sortAll:   [['relevant','सबसे प्रासंगिक'],['newest','नवीनतम'],['salary','वेतन: अधिक से कम']],
      bcJobType: [['Full-time','पूर्णकालिक'],['Part-time','अंशकालिक'],['Contract','अनुबंध'],['Gig','गिग']],
      bcShift:   [['Day','दिन'],['Night','रात'],['Rotational','रोटेशनल'],['Flexible','लचीला']],
      bcSort:    [['relevant','सबसे प्रासंगिक'],['newest','नवीनतम'],['wage','वेतन: अधिक से कम'],['nearest','सबसे नज़दीक']],
    },
    walkin: {
      placeholder:  'शीर्षक, कंपनी, स्थान या कौशल से खोजें…',
      allCities:    'सभी शहर',
      allCompanies: 'सभी कंपनियां',
      allExp:       'सभी अनुभव',
      drivesOf:     (f, total) => `${total} में से ${f} ड्राइव`,
      clearFilters: 'फ़िल्टर हटाएं',
      noActive:     'अभी कोई वॉक-इन ड्राइव नहीं है',
      noActiveDesc: 'जल्द आएंगे — नए ड्राइव नियमित रूप से जोड़े जाते हैं।',
      noMatch:      'आपके फ़िल्टर से कोई ड्राइव नहीं मिली',
    },
  },
};

/* ─── avatar colour palette ──────────────────────────────────── */
const AVATAR_COLORS = ['#6D4CE0','#EA5B22','#059669','#2563EB','#0D9488','#DB2777','#FF9900','#F59E0B','#7C3AED','#1DA462'];
function getColor(str = '') {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function normalizeJob(job) {
  const company = job.company || '';
  return {
    ...job,
    city:      job.location || '',
    posted:    job.posted_on || '',
    type:      job.job_type  || '',
    tags:      [],
    salary:    null,
    exp:       null,
    sponsored: false,
    avatar:    company[0]?.toUpperCase() || '?',
    color:     getColor(company),
  };
}

const MOCK_BC_JOBS = [
  { id: 'b1', title: 'Delivery Executive (2-Wheeler)', company: 'Zepto Dark Store',            city: 'Mumbai',    locality: 'Andheri East', dist: '2.3 km', wage: '₹16,000–22,000/month', shift: 'Rotational', jobType: 'Full-time', openings: 8,  verified: true,  avatar: 'Z', color: '#6D4CE0', posted: '3 hours ago' },
  { id: 'b2', title: 'Electrician',                    company: 'Sharma Electricals',           city: 'Bengaluru', locality: 'Whitefield',   dist: '4.1 km', wage: '₹650–900/day',         shift: 'Day',        jobType: 'Full-time', openings: 3,  verified: true,  avatar: 'S', color: '#EA5B22', posted: 'Today'       },
  { id: 'b3', title: 'Security Guard',                 company: 'Prestige Facility Management', city: 'Pune',      locality: 'Hinjewadi',    dist: '1.8 km', wage: '₹14,000–18,000/month', shift: 'Night',      jobType: 'Full-time', openings: 12, verified: true,  avatar: 'P', color: '#2563EB', posted: '1 day ago'   },
  { id: 'b4', title: 'Driver (Car/Cab)',               company: 'Ola Fleet Partner',            city: 'Delhi NCR', locality: 'Okhla',         dist: '3.5 km', wage: '₹18,000–24,000/month', shift: 'Rotational', jobType: 'Full-time', openings: 5,  verified: true,  avatar: 'O', color: '#0D9488', posted: '2 hours ago' },
  { id: 'b5', title: 'Warehouse Picker',               company: 'Amazon Fulfilment Center',     city: 'Hyderabad', locality: 'Uppal',         dist: '6.2 km', wage: '₹15,000–19,000/month', shift: 'Night',      jobType: 'Full-time', openings: 20, verified: true,  avatar: 'A', color: '#FF9900', posted: 'Today'       },
  { id: 'b6', title: 'Cook / Kitchen Helper',          company: 'Hotel Sarovar',                city: 'Chennai',   locality: 'Guindy',        dist: '2.9 km', wage: '₹14,000–18,000/month', shift: 'Day',        jobType: 'Full-time', openings: 2,  verified: false, avatar: 'H', color: '#DB2777', posted: '3 days ago'  },
  { id: 'b7', title: 'Housekeeping Staff',             company: 'Radisson Blu',                 city: 'Mumbai',    locality: 'Powai',         dist: '3.1 km', wage: '₹13,000–16,000/month', shift: 'Rotational', jobType: 'Full-time', openings: 7,  verified: true,  avatar: 'R', color: '#475569', posted: 'Today'       },
  { id: 'b8', title: 'Welder',                         company: 'L&T Construction',             city: 'Pune',      locality: 'Chakan',        dist: '8.4 km', wage: '₹700–1,000/day',       shift: 'Day',        jobType: 'Contract',  openings: 4,  verified: false, avatar: 'L', color: '#EA5B22', posted: '2 days ago'  },
];

const CITIES = ['Bengaluru', 'Mumbai', 'Delhi NCR', 'Pune', 'Hyderabad', 'Chennai', 'Remote'];

const noScrollbar = { scrollbarWidth: 'none', msOverflowStyle: 'none' };

/* ─── filter dropdown (pill style, portal) ───────────────────── */
/* options: array of [value, label] tuples */
function FilterDropdown({ label, value, onChange, options, clearLabel }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos]   = useState({ top: 0, left: 0, minWidth: 0 });
  const btnRef  = useRef(null);
  const panelRef = useRef(null);

  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 8, left: r.left, minWidth: Math.max(r.width, 180) });
    }
    setOpen(o => !o);
  };

  useEffect(() => {
    if (!open) return;
    const h = e => {
      if (btnRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const displayLabel = value ? (options.find(([v]) => v === value) || [, value])[1] : null;
  const active = Boolean(value);

  return (
    <div className="relative shrink-0">
      <button
        ref={btnRef}
        onClick={toggle}
        className={`flex items-center gap-2 px-4 py-[10px] rounded-full border text-[13.5px] font-semibold whitespace-nowrap transition-all select-none
          ${active || open
            ? 'border-[#12131A] bg-[#F1F0F8] text-[#12131A]'
            : 'border-[#D9D9E4] bg-white text-[#3C3F4A] hover:border-[#3C3F4A] hover:text-[#12131A]'
          }`}
      >
        {displayLabel || label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-150 shrink-0 ${open ? 'rotate-180' : ''} ${active ? 'text-[#12131A]' : 'text-[#6B7280]'}`} />
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, minWidth: pos.minWidth, zIndex: 9999 }}
          className="bg-white border border-[#E7E7EE] rounded-[16px] shadow-[0_20px_60px_rgba(16,20,30,0.14)] overflow-hidden py-2"
        >
          {value && (
            <button
              onClick={() => { onChange(''); setOpen(false); }}
              className="w-full text-left px-4 py-2 text-xs font-semibold text-[#6B7280] hover:bg-[#F7F6FC] transition-colors border-b border-[#E7E7EE] mb-1"
            >
              {clearLabel || 'Clear'}
            </button>
          )}
          {options.map(([val, lbl]) => (
            <button
              key={val}
              onClick={() => { onChange(val); setOpen(false); }}
              className={`w-full text-left px-4 py-[9px] text-[13.5px] transition-colors flex items-center justify-between gap-3
                ${value === val ? 'bg-[#F1F0F8] text-brand-purple font-semibold' : 'text-[#3C3F4A] hover:bg-[#F7F6FC] font-medium'}`}
            >
              {lbl}
              {value === val && <Check className="w-3.5 h-3.5 text-brand-purple shrink-0" />}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

/* ─── mobile filter bottom sheet ────────────────────────────── */
function MobileFilterSheet({ open, onClose, filterDefs, filters, onApply, t }) {
  const [draft, setDraft] = useState({});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) setDraft({ ...filters }); }, [open]);

  return createPortal(
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            key="sheet-scrim"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] bg-[rgba(16,17,25,0.4)]"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      <div
        className="fixed bottom-0 left-0 right-0 z-[91] bg-white overflow-y-auto"
        style={{
          borderRadius: '22px 22px 0 0',
          padding: '18px 18px calc(18px + env(safe-area-inset-bottom))',
          maxHeight: '78vh',
          boxShadow: '0 20px 60px rgba(16,20,30,0.14)',
          transform: open ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.25s cubic-bezier(0.2,0.8,0.2,1)',
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-[19px] text-[#12131A] tracking-tight">{t.filters}</h3>
          <button onClick={onClose} className="w-[38px] h-[38px] rounded-full flex items-center justify-center text-[#3C3F4A] hover:bg-[#F1F0F8] transition-colors">
            <X className="w-[18px] h-[18px]" />
          </button>
        </div>

        {filterDefs.map(def => (
          <div key={def.key} className="mb-5">
            <span className="block text-[11.5px] font-bold uppercase tracking-[0.08em] text-[#3C3F4A] mb-2.5">{def.label}</span>
            <div className="flex flex-wrap gap-2">
              {def.options.map(([val, lbl]) => {
                const sel = (draft[def.key] || '') === val && val !== '';
                return (
                  <button
                    key={val || '__any'}
                    onClick={() => setDraft(d => ({ ...d, [def.key]: sel ? '' : val }))}
                    className={`px-[14px] py-[9px] rounded-full border text-[13px] font-semibold transition-all
                      ${sel ? 'bg-[#12131A] text-white border-[#12131A]' : 'bg-white text-[#3C3F4A] border-[#D9D9E4]'}`}
                  >
                    {lbl}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <button
          onClick={() => { onApply(draft); onClose(); }}
          className="sticky bottom-0 w-full py-[13px] rounded-full bg-brand-purple text-white font-bold text-[14px] hover:brightness-110 transition-all shadow-purple mt-1"
        >
          {t.showResults}
        </button>
      </div>
    </>,
    document.body
  );
}

/* ─── auth gate modal ────────────────────────────────────────── */
function AuthGateModal({ onClose, onLogin, onSignup, t }) {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
        transition={{ type: 'spring', damping: 28, stiffness: 320 }}
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-t-3xl sm:rounded-2xl shadow-glass-xl px-6 pb-10 pt-5 sm:p-8 w-full sm:max-w-sm text-center"
      >
        <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5 sm:hidden" />
        <div className="w-14 h-14 rounded-2xl bg-brand-purple/10 flex items-center justify-center mx-auto mb-4">
          <svg className="w-7 h-7 text-brand-purple" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
          </svg>
        </div>
        <h2 className="font-display font-black text-xl text-ink-900 mb-2">{t.signIn}</h2>
        <p className="text-sm text-ink-500 mb-6">{t.signInDesc}</p>
        <div className="flex flex-col gap-3">
          <button onClick={onLogin} className="w-full py-3.5 rounded-xl font-black text-sm text-white bg-brand-purple hover:brightness-110 active:scale-95 transition-all shadow-purple">{t.login}</button>
          <button onClick={onSignup} className="w-full py-3.5 rounded-xl font-black text-sm text-ink-700 border border-gray-200 hover:bg-gray-50 transition-colors">{t.createAccount}</button>
        </div>
        <button onClick={onClose} className="mt-4 text-xs text-ink-300 hover:text-ink-500 transition-colors">{t.continueBrowsing}</button>
      </motion.div>
    </motion.div>
  );
}

/* ─── featured strip ─────────────────────────────────────────── */
function FeaturedStrip({ jobs, onClose, t }) {
  const featured = jobs.filter(j => j.sponsored).slice(0, 6);
  if (!featured.length) return null;
  return (
    <div className="border border-[#E7E7EE] rounded-[20px] bg-white p-5 mb-6"
         style={{ boxShadow: '0 1px 2px rgba(16,20,30,0.05)' }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-[0.04em] px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-100">
            ★ {t.featured}
          </span>
          <span className="font-bold text-[18px] text-[#12131A] leading-none">{t.sponsored}</span>
        </div>
        <button
          onClick={onClose}
          title={t.hideSponsored}
          className="w-[28px] h-[28px] rounded-full border border-[#E7E7EE] flex items-center justify-center text-[#6B7280] hover:bg-red-50 hover:border-red-200 hover:text-red-400 transition-all shrink-0"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="flex gap-3.5 overflow-x-auto pb-1" style={noScrollbar}>
        {featured.map(j => (
          <div key={j.id}
               className="flex-none w-[200px] border border-[#E7E7EE] rounded-2xl p-4 bg-[#F7F6FC] cursor-pointer hover:border-brand-purple/40 transition-all group">
            <div className="w-[38px] h-[38px] rounded-[10px] flex items-center justify-center font-bold text-sm text-white mb-3"
                 style={{ background: j.color }}>{j.avatar}</div>
            <h4 className="font-bold text-[15px] text-[#12131A] leading-snug mb-0.5 group-hover:text-brand-purple transition-colors">{j.title}</h4>
            <span className="block text-[12px] text-[#6B7280] mb-1.5">{j.company} · {j.city}</span>
            <span className="font-bold text-[13px] text-[#12131A]">{j.salary}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── job row (broadsheet 3-col) ─────────────────────────────── */
function JobRow({ job, saved, onSave, onApply, onJobClick, t }) {
  const meta = [job.salary, job.exp, job.type].filter(Boolean);

  const body = (
    <div className="min-w-0">
      <div className="flex items-center flex-wrap gap-1.5 mb-1.5">
        <span className="text-[13px] font-semibold text-[#6B7280]">{job.company}{job.city ? ` · ${job.city}` : ''}</span>
        {job.sponsored && (
          <span className="text-[10px] font-black uppercase tracking-[0.04em] px-2 py-[3px] rounded-full bg-amber-50 text-amber-700 border border-amber-100">
            {t.featured}
          </span>
        )}
      </div>
      <h3 className="font-bold text-[18px] sm:text-[19.5px] text-[#12131A] leading-snug mb-2.5 group-hover:text-brand-purple transition-colors">
        {job.title}
      </h3>
      {(job.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2.5">
          {job.tags.slice(0, 4).map(tag => (
            <span key={tag} className="text-[11px] font-semibold px-2.5 py-[3px] rounded-full bg-[#F1F0F8] text-[#3C3F4A]">{tag}</span>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13.5px] text-[#3C3F4A] font-medium">
        {meta.map((v, i) => (
          <React.Fragment key={i}>
            <span>{v}</span>
            <span className="text-[#D9D9E4]">·</span>
          </React.Fragment>
        ))}
        <span className="text-[#6B7280] font-normal">{job.posted}</span>
      </div>
      <span className="inline-flex items-center gap-1 mt-2.5 text-[12.5px] font-semibold text-brand-purple group-hover:underline">
        View details <ChevronDown className="w-3 h-3 -rotate-90" />
      </span>
    </div>
  );

  return (
    <article
      className="group border-b border-[#E7E7EE] cursor-pointer hover:bg-[#F7F6FC] -mx-4 px-4 sm:mx-0 sm:px-0 transition-colors"
      onClick={() => onJobClick?.(job)}
    >
      <div className="hidden sm:grid items-start gap-6 py-6" style={{ gridTemplateColumns: '44px 1fr 168px' }}>
        <div className="w-11 h-11 rounded-xl flex items-center justify-center font-bold text-base text-white shrink-0"
             style={{ background: job.color }}>{job.avatar}</div>
        {body}
        <div className="flex flex-col items-end gap-3 pt-0.5">
          <button onClick={e => { e.stopPropagation(); onSave(job.id); }}
            className={`w-[38px] h-[38px] rounded-full flex items-center justify-center transition-all ${saved ? 'text-brand-saffron' : 'text-[#3C3F4A] hover:bg-[#F1F0F8]'}`}>
            {saved ? <BookmarkCheck className="w-[18px] h-[18px]" /> : <Bookmark className="w-[18px] h-[18px]" />}
          </button>
          <button onClick={e => { e.stopPropagation(); onApply(job); }}
            className="px-[15px] py-2 rounded-full bg-brand-purple text-white text-[13px] font-bold hover:brightness-110 active:scale-95 transition-all shadow-purple whitespace-nowrap">
            {t.easyApply}
          </button>
        </div>
      </div>

      <div className="sm:hidden py-5">
        {body}
        <div className="flex items-center gap-2 mt-3">
          <button onClick={e => { e.stopPropagation(); onSave(job.id); }}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full border text-[13px] font-semibold transition-all shrink-0
              ${saved ? 'text-brand-saffron border-brand-saffron/40 bg-orange-50' : 'text-[#6B7280] border-[#D9D9E4]'}`}>
            {saved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
            {saved ? t.saved : t.save}
          </button>
          <button onClick={e => { e.stopPropagation(); onApply(job); }}
            className="flex-1 py-2.5 rounded-full bg-brand-purple text-white text-[13px] font-bold text-center hover:brightness-110 active:scale-95 transition-all shadow-purple">
            {t.easyApply}
          </button>
        </div>
      </div>
    </article>
  );
}

/* ─── blue collar row ────────────────────────────────────────── */
function BCJobRow({ job, saved, onSave, onApply, t }) {
  const body = (
    <div className="min-w-0">
      <div className="flex items-center flex-wrap gap-1.5 mb-1.5">
        <span className="text-[13px] font-semibold text-[#6B7280]">{job.company}</span>
        {job.verified && (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-[3px] rounded-full bg-green-50 text-green-700 border border-green-100">
            <Check className="w-2.5 h-2.5" /> {t.verified}
          </span>
        )}
      </div>
      <h3 className="font-bold text-[18px] sm:text-[19.5px] text-[#12131A] leading-snug mb-1.5 group-hover:text-brand-saffron transition-colors">
        {job.title}
      </h3>
      <div className="font-extrabold text-[19px] text-[#12131A] mb-2 tracking-tight leading-none">{job.wage}</div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13.5px] text-[#3C3F4A] font-medium">
        <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5 text-[#6B7280] shrink-0" />{job.openings} {t.openings}</span>
        <span className="text-[#D9D9E4]">·</span>
        <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-[#6B7280] shrink-0" />{job.shift} {t.shift}</span>
        <span className="text-[#D9D9E4]">·</span>
        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[#6B7280] shrink-0" />{job.locality}, {job.dist}</span>
        <span className="text-[#D9D9E4]">·</span>
        <span className="text-[#6B7280] font-normal">{job.jobType}</span>
        <span className="text-[#D9D9E4]">·</span>
        <span className="text-[#6B7280] font-normal">{job.posted}</span>
      </div>
    </div>
  );

  return (
    <article className="group border-b border-[#E7E7EE] cursor-pointer hover:bg-[#F7F6FC] -mx-4 px-4 sm:mx-0 sm:px-0 transition-colors">
      <div className="hidden sm:grid items-start gap-6 py-6" style={{ gridTemplateColumns: '44px 1fr 168px' }}>
        <div className="w-11 h-11 rounded-xl flex items-center justify-center font-bold text-base text-white shrink-0"
             style={{ background: job.color }}>{job.avatar}</div>
        {body}
        <div className="flex flex-col items-end gap-3 pt-0.5">
          <button onClick={e => { e.stopPropagation(); onSave(job.id); }}
            className={`w-[38px] h-[38px] rounded-full flex items-center justify-center transition-all ${saved ? 'text-brand-saffron' : 'text-[#3C3F4A] hover:bg-[#F1F0F8]'}`}>
            {saved ? <BookmarkCheck className="w-[18px] h-[18px]" /> : <Bookmark className="w-[18px] h-[18px]" />}
          </button>
          <button onClick={e => { e.stopPropagation(); onApply(job); }}
            className="flex items-center gap-1.5 px-[15px] py-2 rounded-full bg-brand-saffron text-white text-[13px] font-bold hover:brightness-110 active:scale-95 transition-all shadow-saffron whitespace-nowrap">
            <Phone className="w-3.5 h-3.5" /> {t.callNow}
          </button>
        </div>
      </div>

      <div className="sm:hidden py-5">
        {body}
        <div className="flex items-center gap-2 mt-3">
          <button onClick={e => { e.stopPropagation(); onSave(job.id); }}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full border text-[13px] font-semibold transition-all shrink-0
              ${saved ? 'text-brand-saffron border-brand-saffron/40 bg-orange-50' : 'text-[#6B7280] border-[#D9D9E4]'}`}>
            {saved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
            {t.save}
          </button>
          <button onClick={e => { e.stopPropagation(); onApply(job); }}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-full bg-brand-saffron text-white text-[13px] font-bold hover:brightness-110 active:scale-95 transition-all">
            <Phone className="w-4 h-4" /> {t.callNow}
          </button>
        </div>
      </div>
    </article>
  );
}

/* ─── walk-in drives panel ───────────────────────────────────── */
function WalkinPanel({ t }) {
  const [drives,     setDrives]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [city,       setCity]       = useState('');
  const [company,    setCompany]    = useState('');
  const [experience, setExperience] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await walkinDriveService.list();
        const backendOnly = data.filter(d => !WALKIN_DRIVES.some(h => h.id === d.id));
        if (active) setDrives([...backendOnly, ...WALKIN_DRIVES]);
      } catch {
        if (active) setDrives(WALKIN_DRIVES);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const getCity = d => (d.city && d.city.trim()) || (d.venue ? d.venue.split(',').pop().trim() : '');
  const cities      = useMemo(() => [...new Set(drives.map(getCity).filter(Boolean))].sort(), [drives]);
  const companies   = useMemo(() => [...new Set(drives.map(d => d.company).filter(Boolean))].sort(), [drives]);
  const experiences = useMemo(() => [...new Set(drives.map(d => d.meta?.experience).filter(Boolean))].sort(), [drives]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return drives.filter(d => {
      if (city && getCity(d) !== city) return false;
      if (company && d.company !== company) return false;
      if (experience && d.meta?.experience !== experience) return false;
      if (q) {
        const hay = [d.title, d.company, d.venue, d.meta?.role, ...(d.keySkills || [])]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [drives, search, city, company, experience]);

  const hasFilters = Boolean(search || city || company || experience);
  const clearFilters = () => { setSearch(''); setCity(''); setCompany(''); setExperience(''); };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-[38px] h-[38px] rounded-full border-2 border-[#E7E7EE] border-t-brand-purple animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {drives.length > 0 && (
        <div className="border border-[#E7E7EE] rounded-[18px] bg-white p-4 sm:p-5 mb-5"
             style={{ boxShadow: '0 1px 2px rgba(16,20,30,0.05)' }}>
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder={t.walkin.placeholder}
              className="w-full rounded-full border border-[#D9D9E4] bg-white pl-10 pr-9 py-3 text-[14px] font-medium text-[#12131A] placeholder-[#6B7280] outline-none focus:border-brand-purple transition-colors"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#12131A]">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <FilterSelect icon={MapPin}    value={city}       onChange={e => setCity(e.target.value)}       allLabel={t.walkin.allCities}    options={cities} />
            <FilterSelect icon={Building2} value={company}    onChange={e => setCompany(e.target.value)}    allLabel={t.walkin.allCompanies} options={companies} />
            <FilterSelect icon={Clock}     value={experience} onChange={e => setExperience(e.target.value)} allLabel={t.walkin.allExp}       options={experiences} />
          </div>
          {hasFilters && (
            <div className="mt-3 flex items-center justify-between">
              <span className="text-[13px] font-semibold text-[#6B7280]">{t.walkin.drivesOf(filtered.length, drives.length)}</span>
              <button onClick={clearFilters} className="inline-flex items-center gap-1 text-[13px] font-bold text-brand-purple hover:underline">
                <X className="w-3.5 h-3.5" /> {t.walkin.clearFilters}
              </button>
            </div>
          )}
        </div>
      )}

      {drives.length === 0 ? (
        <div className="text-center py-16">
          <p className="font-bold text-[20px] text-[#12131A] mb-1.5">{t.walkin.noActive}</p>
          <p className="text-[14px] text-[#6B7280]">{t.walkin.noActiveDesc}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <p className="font-bold text-[20px] text-[#12131A] mb-4">{t.walkin.noMatch}</p>
          <button onClick={clearFilters} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-purple text-white text-[13px] font-bold hover:brightness-110 active:scale-95 transition-all">
            <X className="w-4 h-4" /> {t.walkin.clearFilters}
          </button>
        </div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-4 sm:space-y-5">
          {filtered.map(drive => <DriveCard key={drive.id} drive={drive} />)}
        </motion.div>
      )}
    </div>
  );
}

/* ─── job detail modal ───────────────────────────────────── */
function JobDetailModal({ job, onClose, t }) {
  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const applyUrl = job.link || job.external_details?.external_url || job.source;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ type: 'spring', damping: 28, stiffness: 320 }}
          onClick={e => e.stopPropagation()}
          className="bg-white rounded-t-3xl sm:rounded-2xl shadow-[0_20px_60px_rgba(16,20,30,0.18)] w-full sm:max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-start gap-4 p-6 border-b border-[#E7E7EE]">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-base text-white shrink-0"
              style={{ background: job.color }}
            >
              {job.avatar}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-bold text-[20px] text-[#12131A] leading-snug mb-0.5">{job.title}</h2>
              <p className="text-[14px] text-[#6B7280] font-medium">{job.company}{job.city ? ` · ${job.city}` : ''}</p>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full border border-[#E7E7EE] flex items-center justify-center text-[#6B7280] hover:bg-[#F1F0F8] transition-colors shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Meta pills */}
          <div className="flex flex-wrap gap-2 px-6 pt-4">
            {job.type && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F1F0F8] text-[#3C3F4A] text-[12.5px] font-semibold">
                <Briefcase className="w-3 h-3" /> {job.type}
              </span>
            )}
            {job.city && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F1F0F8] text-[#3C3F4A] text-[12.5px] font-semibold">
                <MapPin className="w-3 h-3" /> {job.city}
              </span>
            )}
            {job.posted && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F1F0F8] text-[#3C3F4A] text-[12.5px] font-semibold">
                <Calendar className="w-3 h-3" /> {job.posted}
              </span>
            )}
            {job.salary && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F1F0F8] text-[#3C3F4A] text-[12.5px] font-semibold">
                {job.salary}
              </span>
            )}
          </div>

          {/* Description */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {job.description ? (
              <div>
                <h3 className="font-bold text-[15px] text-[#12131A] mb-3">Job Description</h3>
                <p className="text-[14px] text-[#3C3F4A] leading-relaxed whitespace-pre-line">{job.description}</p>
              </div>
            ) : (
              <p className="text-[14px] text-[#6B7280] italic">No description available. Click Apply to view full details on the company's website.</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 p-6 border-t border-[#E7E7EE]">
            <button
              onClick={onClose}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-[#D9D9E4] text-[#3C3F4A] text-[13px] font-semibold hover:border-[#3C3F4A] transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
            {applyUrl && (
              <a
                href={applyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full bg-brand-purple text-white text-[13px] font-bold hover:brightness-110 active:scale-95 transition-all shadow-purple"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Apply Now
              </a>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

/* ─── main page ──────────────────────────────────────────────── */
export default function SearchPage({ user, isAuthenticated, onLogout, landing }) {
  const navigate       = useNavigate();
  const [showResumeBanner, setShowResumeBanner] = useState(
    () => localStorage.getItem('resumeBannerDismissed') !== '1'
  );
  const dismissResumeBanner = () => {
    localStorage.setItem('resumeBannerDismissed', '1');
    setShowResumeBanner(false);
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useParams();

  const [lang, setLang] = useState('en');
  const t = T[lang];

  // Landing-page context (Phase 5b): /jobs-in-:city and /jobs/:category resolve
  // to real, self-canonical pages with unique meta + original intro copy.
  const landingCtx = useMemo(() => {
    if (landing === 'location' && params.city) {
      const label = cityLabelFromSlug(params.city);
      return { kind: 'location', label, ...locationIntro(label) };
    }
    if (landing === 'category' && params.category) {
      const label = categoryLabelFromSlug(params.category);
      return { kind: 'category', label, ...categoryIntro(label) };
    }
    return null;
  }, [landing, params.city, params.category]);

  const [role,     setRole]     = useState(
    searchParams.get('q') || (landingCtx?.kind === 'category' ? landingCtx.label : '')
  );
  const [location, setLocation] = useState(
    searchParams.get('loc') || (landingCtx?.kind === 'location' ? landingCtx.label : '')
  );
  const [activeTab, setActiveTab] = useState(
    searchParams.get('tab') === 'blue-collar' ? 'bc' :
    searchParams.get('tab') === 'walkin'      ? 'walkin' : 'all'
  );

  /* all-jobs filters — values are always English keys */
  const [exp,     setExp]     = useState('');
  const [sal,     setSal]     = useState('');
  const [jobType, setJobType] = useState('');
  const [posted,  setPosted]  = useState('');
  const [sort,    setSort]    = useState('');

  /* blue-collar filters */
  const [bcJobType, setBcJobType] = useState('');
  const [bcShift,   setBcShift]   = useState('');
  const [bcSort,    setBcSort]    = useState('');

  /* api state */
  const [jobs,        setJobs]        = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsError,   setJobsError]   = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  /* misc */
  const [sheetOpen,        setSheetOpen]        = useState(false);
  const [saved,            setSaved]            = useState(new Set());
  const [showAuthGate,     setShowAuthGate]     = useState(false);
  const [recentSearches,   setRecentSearches]   = useState([]);
  const [sponsoredVisible, setSponsoredVisible] = useState(true);
  const [selectedJob,      setSelectedJob]      = useState(null);

  const fetchJobs = async (title, loc) => {
    setJobsLoading(true);
    setJobsError(null);
    setHasSearched(true);
    try {
      const params = new URLSearchParams();
      if (title) params.set('job_title', title);
      if (loc)   params.set('location', loc);
      params.set('source', 'recruiter');
      params.set('num_jobs', '20');
      const res = await fetch(`${config.API_BASE_URL}/search?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const normalised = Array.isArray(data) ? data.map(normalizeJob) : [];
      setJobs(normalised);
    } catch (e) {
      // DEV-ONLY: still show test card even when backend is down
      if (process.env.NODE_ENV === 'development') {
        setJobs([normalizeJob({
          id: '__dev_test_job__',
          title: 'Senior Frontend Engineer (Test Card)',
          description: 'This is a hardcoded dev-only job to test the detail modal.\n\nResponsibilities:\n- Build pixel-perfect UIs\n- Write clean, maintainable React code\n- Collaborate with designers and backend engineers\n\nRequirements:\n- 3+ years of React experience\n- Strong CSS / Tailwind skills\n- Familiarity with REST APIs\n\nPerks:\n- Remote-friendly\n- Competitive pay\n- Great team',
          company: 'JobxBharat (Dev)',
          location: 'Bengaluru, India',
          job_type: 'full-time',
          link: 'https://example.com/apply',
          posted_on: 'just now',
          source: 'https://example.com/apply',
          external_details: { external_url: 'https://example.com/apply' },
        })]);
      } else {
        setJobsError(e.message);
        setJobs([]);
      }
    } finally {
      setJobsLoading(false);
    }
  };

  // Signal the prerender crawler once the jobs list has loaded.
  usePrerenderReady(!jobsLoading);

  // Per-route meta (Phase 2). Canonical resolves to the clean /search path
  // (filter/sort/tracking query params stripped) via useSEO's pathname base.
  const pageSeo = useMemo(() => {
    // Landing pages (/jobs-in-:city, /jobs/:category) own their title + a
    // self-canonical URL (useSEO derives canonical from the real pathname).
    if (landingCtx) {
      const title = `${landingCtx.heading} | JobxBharat`;
      const description = landingCtx.body.slice(0, 160);
      return { title, description, ogTitle: title, ogDescription: description };
    }
    const loc = location.trim();
    const roleQ = role.trim();
    let title, description;
    if (activeTab === 'walkin') {
      title = `Walk-in Drives${loc ? ` in ${loc}` : ' in India'} | JobxBharat`;
      description = `Find the latest walk-in drives${loc ? ` in ${loc}` : ' across India'} — dates, venues, roles and eligibility, updated regularly on JobxBharat.`;
    } else if (roleQ && loc) {
      title = `${roleQ} Jobs in ${loc} | JobxBharat`;
      description = `Explore ${roleQ} jobs in ${loc}. Browse current openings from across the web and apply on JobxBharat.`;
    } else if (loc) {
      title = `Jobs in ${loc} | JobxBharat`;
      description = `Explore the latest job openings in ${loc} across roles and companies on JobxBharat.`;
    } else if (roleQ) {
      title = `${roleQ} Jobs in India | JobxBharat`;
      description = `Explore ${roleQ} jobs across India. Browse current openings and apply on JobxBharat.`;
    } else {
      title = 'Find Jobs in India | JobxBharat';
      description = 'Search white-collar, blue-collar and walk-in jobs across India on JobxBharat — updated daily.';
    }
    return { title, description, ogTitle: title, ogDescription: description };
  }, [activeTab, role, location, landingCtx]);

  useSEO(pageSeo);

  /* auto-open job modal when jobId is in the URL */
  useEffect(() => {
    if (jobsLoading || jobs.length === 0) return;
    const jobId = searchParams.get('jobId');
    if (!jobId) return;
    const match = jobs.find(j => j.id === jobId);
    if (match) setSelectedJob(match);
  }, [jobs, jobsLoading]); // eslint-disable-line react-hooks/exhaustive-deps


  /* fetch on mount — use query params or landing-page presets (role/location
     are already seeded from searchParams or the landing city/category). */
  useEffect(() => {
    fetchJobs(role, location);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    if (role.trim()) setRecentSearches(p => [role.trim(), ...p.filter(r => r !== role.trim())].slice(0, 5));
    fetchJobs(role, location);
  };
  const handleApply = job => {
    const url = job.link || job.external_details?.external_url || job.source;
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };
  const handleJobClick = job => {
    setSelectedJob(job);
    setSearchParams(p => { const n = new URLSearchParams(p); n.set('jobId', job.id); return n; }, { replace: true });
  };
  const handleCloseDetail = () => {
    setSelectedJob(null);
    setSearchParams(p => { const n = new URLSearchParams(p); n.delete('jobId'); return n; }, { replace: true });
  };
  const handleSave  = id => {
    if (!isAuthenticated) { setShowAuthGate(true); return; }
    setSaved(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  /* filter defs (language-aware) */
  const ALL_FILTER_DEFS = [
    { key: 'exp',  label: t.filterLabels.exp,  options: t.filterOpts.exp  },
    { key: 'sal',  label: t.filterLabels.sal,  options: t.filterOpts.sal  },
    { key: 'type', label: t.filterLabels.type, options: t.filterOpts.type },
  ];
  const BC_FILTER_DEFS = [
    { key: 'jobType', label: t.filterLabels.type,  options: t.filterOpts.bcJobType },
    { key: 'shift',   label: t.filterLabels.posted, options: t.filterOpts.bcShift  },
  ];

  /* sheet filter state */
  const sheetFilters = activeTab === 'all'
    ? { exp, sal, type: jobType }
    : { jobType: bcJobType, shift: bcShift };

  const applySheet = draft => {
    if (activeTab === 'all') {
      setExp(draft.exp || ''); setSal(draft.sal || ''); setJobType(draft.type || '');
    } else {
      setBcJobType(draft.jobType || ''); setBcShift(draft.shift || '');
    }
  };

  const JOBS_PER_PAGE = 8;
  const [currentPage, setCurrentPage] = useState(1);

  /* reset page when results or filters change */
  useEffect(() => { setCurrentPage(1); }, [jobs, jobType]);

  /* client-side filter on top of API results */
  const filteredJobs = useMemo(() => {
    let list = jobs;
    if (jobType) list = list.filter(j => j.type === jobType);
    return list;
  }, [jobs, jobType]);

  const totalPages   = Math.ceil(filteredJobs.length / JOBS_PER_PAGE);
  const pagedJobs    = filteredJobs.slice((currentPage - 1) * JOBS_PER_PAGE, currentPage * JOBS_PER_PAGE);

  const filteredBC = useMemo(() => {
    let jobs = MOCK_BC_JOBS;
    if (role.trim()) jobs = jobs.filter(j =>
      j.title.toLowerCase().includes(role.toLowerCase()) ||
      j.company.toLowerCase().includes(role.toLowerCase())
    );
    if (location.trim()) jobs = jobs.filter(j => j.city.toLowerCase().includes(location.toLowerCase()));
    if (bcJobType) jobs = jobs.filter(j => j.jobType === bcJobType);
    if (bcShift)   jobs = jobs.filter(j => j.shift   === bcShift);
    return jobs;
  }, [role, location, bcJobType, bcShift]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar
        onSignIn={() => navigate('/login')}
        onPostJob={() => window.open('https://recruiter.jobxbharat.com/', '_blank')}
        user={user}
        onLogout={onLogout}
        isAuthenticated={isAuthenticated}
      />

      {/* ── SEARCH HERO BAND ─────────────────────────────────────── */}
      <section
        className="border-b border-[#E7E7EE] pt-[64px] md:pt-[72px]"
        style={{
          background:
            'radial-gradient(680px 340px at 82% 0%, rgba(255,230,200,0.55), transparent 60%),' +
            'radial-gradient(620px 320px at 10% 10%, rgba(220,210,255,0.6), transparent 60%),' +
            '#F7F6FC',
        }}
      >
        <div className="max-w-5xl mx-auto px-4 py-7 sm:py-10">

          {/* Title + language toggle */}
          <div className="flex items-start justify-between gap-4 mb-5">
            <h1 className="font-display font-black text-[26px] sm:text-[38px] md:text-[44px] text-[#12131A] leading-[1.08] tracking-tight max-w-[18ch]">
              {t.titleParts[0]}
              <span className="text-brand-saffron">{t.titleParts[1]}</span>
              {t.titleParts[2]}
            </h1>

            {/* Language toggle */}
            <button
              onClick={() => setLang(l => l === 'en' ? 'hi' : 'en')}
              className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#D9D9E4] bg-white text-[12.5px] font-bold text-[#3C3F4A] hover:border-[#3C3F4A] hover:text-[#12131A] transition-all shadow-[0_1px_2px_rgba(16,20,30,0.06)] mt-1"
            >
              <Globe className="w-3.5 h-3.5" />
              {lang === 'en' ? 'हिंदी' : 'English'}
            </button>
          </div>

          {/* Search box */}
          <div className="flex flex-col sm:flex-row items-stretch bg-white border border-[#D9D9E4] sm:rounded-full rounded-[20px] p-[6px] mb-4"
               style={{ boxShadow: '0 1px 2px rgba(16,20,30,0.04), 0 10px 28px rgba(16,20,30,0.07)' }}>
            <div className="flex items-center gap-2.5 px-4 py-3 sm:py-2.5 flex-1 min-w-0">
              <Search className="w-4 h-4 text-[#6B7280] shrink-0" />
              <input
                type="text" value={role}
                onChange={e => setRole(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder={t.searchPlaceholder}
                className="flex-1 bg-transparent text-[14.5px] font-medium text-[#12131A] placeholder-[#6B7280] outline-none min-w-0"
              />
              {role && (
                <button onClick={() => setRole('')} className="text-[#6B7280] hover:text-[#12131A] shrink-0">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="hidden sm:block w-px bg-[#E7E7EE] my-2" />
            <div className="sm:hidden h-px bg-[#E7E7EE] mx-4" />
            <div className="flex items-center gap-2.5 px-4 py-3 sm:py-2.5 flex-1 min-w-0">
              <MapPin className="w-4 h-4 text-[#6B7280] shrink-0" />
              <input
                type="text" value={location}
                onChange={e => setLocation(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder={t.locPlaceholder}
                className="flex-1 bg-transparent text-[14.5px] font-medium text-[#12131A] placeholder-[#6B7280] outline-none min-w-0"
              />
            </div>
            <button
              onClick={handleSearch}
              className="mx-[6px] mb-[6px] sm:mb-0 px-6 py-3 sm:py-[13px] rounded-full bg-brand-purple text-white font-bold text-[14px] hover:brightness-110 active:scale-95 transition-all shadow-purple flex justify-center"
            >
              {t.searchBtn}
            </button>
          </div>

          {/* City pills */}
          <div className="flex items-center gap-2 overflow-x-auto pb-0.5" style={noScrollbar}>
            <span className="text-[13.5px] font-semibold text-[#6B7280] shrink-0">{t.popular}</span>
            {CITIES.map(c => (
              <button key={c} onClick={() => setLocation(c)}
                className={`shrink-0 px-[17px] py-2 rounded-full border text-[13.5px] font-semibold transition-all
                  ${location === c ? 'bg-[#12131A] text-white border-[#12131A]' : 'bg-white border-[#D9D9E4] text-[#3C3F4A] hover:border-[#3C3F4A]'}`}>
                {c}
              </button>
            ))}
          </div>

          {/* Recent searches */}
          {recentSearches.length > 0 && (
            <div className="flex items-center gap-2 mt-2.5 overflow-x-auto pb-0.5" style={noScrollbar}>
              <span className="text-[13px] font-semibold text-[#6B7280] shrink-0">{t.recent}</span>
              {recentSearches.map(q => (
                <button key={q} onClick={() => setRole(q)}
                  className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-brand-purple/20 bg-brand-purple/5 text-brand-purple text-[13px] font-semibold hover:bg-brand-purple/10 transition-colors">
                  {q}
                  <X className="w-3 h-3 opacity-60" onClick={e => { e.stopPropagation(); setRecentSearches(p => p.filter(r => r !== q)); }} />
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── MAIN CONTENT ─────────────────────────────────────────── */}
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 pt-6 pb-24">

        {/* Resume builder promo banner */}
        {showResumeBanner && (
          <div className="relative flex flex-col sm:flex-row sm:items-center gap-3 mb-6 p-4 sm:p-5 rounded-2xl border border-brand-purple/25 bg-gradient-to-r from-brand-purple/10 to-transparent">
            <span className="w-10 h-10 rounded-xl bg-brand-purple/15 flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 text-brand-purple" />
            </span>
            <div className="flex-1 min-w-0 pr-6 sm:pr-0">
              <p className="font-bold text-[14.5px] text-[#12131A]">Build an ATS-friendly resume in minutes ✨</p>
              <p className="text-[12.5px] text-[#6B7280]">Free AI resume builder — answer simple questions, download a professional PDF.</p>
            </div>
            <button
              onClick={() => navigate('/resume-builder')}
              className="shrink-0 px-5 py-2.5 rounded-full bg-brand-purple text-white text-[13.5px] font-bold hover:bg-brand-deep transition-colors"
            >
              Build now
            </button>
            <button
              onClick={dismissResumeBanner}
              className="absolute top-3 right-3 p-1 rounded-full text-[#6B7280] hover:bg-[#F1F0F8]"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Landing intro copy (category / location pages) — original copy so
            these aren't thin listing-only pages */}
        {landingCtx && (
          <section className="mb-6">
            <h2 className="font-display font-black text-[22px] sm:text-[26px] text-[#12131A] tracking-tight mb-2">
              {landingCtx.heading}
            </h2>
            <p className="text-[14px] sm:text-[15px] text-[#3C3F4A] leading-relaxed max-w-3xl">
              {landingCtx.body}
            </p>
          </section>
        )}

        {/* Segmented tabs */}
        <div className="flex items-center p-1.5 bg-[#F1F0F8] rounded-full w-fit max-w-full overflow-x-auto mb-6" style={noScrollbar}>
          {(['all', 'bc', 'walkin']).map(id => (
            <button key={id} onClick={() => setActiveTab(id)}
              className={`shrink-0 px-5 py-[11px] rounded-full text-[14px] font-bold whitespace-nowrap transition-all
                ${activeTab === id ? 'bg-[#12131A] text-white shadow-[0_1px_2px_rgba(16,20,30,0.1)]' : 'text-[#3C3F4A] hover:text-[#12131A]'}`}>
              {t.tabs[id]}
            </button>
          ))}
        </div>

        {/* ── ALL JOBS ───────────────────────────────────────────── */}
        {activeTab === 'all' && (
          <div>
            <div className="flex items-center gap-2.5 pb-4 border-b border-[#E7E7EE] mb-5 overflow-x-auto" style={noScrollbar}>
              <div className="hidden sm:flex items-center gap-2.5 flex-1 min-w-0 overflow-x-auto" style={noScrollbar}>
                <FilterDropdown label={t.filterLabels.exp}    value={exp}     onChange={setExp}     options={t.filterOpts.exp}     clearLabel={t.clear} />
                <FilterDropdown label={t.filterLabels.sal}    value={sal}     onChange={setSal}     options={t.filterOpts.sal}     clearLabel={t.clear} />
                <FilterDropdown label={t.filterLabels.type}   value={jobType} onChange={setJobType} options={t.filterOpts.type}    clearLabel={t.clear} />
                <FilterDropdown label={t.filterLabels.posted} value={posted}  onChange={setPosted}  options={t.filterOpts.posted}  clearLabel={t.clear} />
              </div>
              <button onClick={() => setSheetOpen(true)}
                className="sm:hidden flex items-center gap-2 px-4 py-[10px] rounded-full border border-[#D9D9E4] bg-white text-[13.5px] font-semibold text-[#3C3F4A] shrink-0">
                <SlidersHorizontal className="w-3.5 h-3.5" /> {t.filters}
              </button>
              <div className="flex items-center gap-2 ml-auto shrink-0">
                <span className="hidden sm:block text-[13.5px] font-semibold text-[#6B7280]">{t.sort}</span>
                <FilterDropdown label={t.filterLabels.relevant} value={sort} onChange={setSort} options={t.filterOpts.sortAll} clearLabel={t.clear} />
              </div>
            </div>

            {hasSearched && !jobsLoading && (
              <p className="text-[13.5px] text-[#6B7280] font-medium mb-5">
                {t.rolesFound(filteredJobs.length)}
              </p>
            )}

            {jobsLoading && (
              <div className="flex justify-center py-16">
                <div className="w-[38px] h-[38px] rounded-full border-2 border-[#E7E7EE] border-t-brand-purple animate-spin" />
              </div>
            )}

            {jobsError && !jobsLoading && (
              <div className="text-center py-16">
                <p className="font-bold text-[20px] text-[#12131A] mb-1.5">Could not load jobs</p>
                <p className="text-[14px] text-[#6B7280]">{jobsError}</p>
              </div>
            )}

            {!jobsLoading && !jobsError && hasSearched && filteredJobs.length === 0 && (
              <div className="text-center py-16">
                <p className="font-bold text-[20px] text-[#12131A] mb-1.5">{t.noRoles}</p>
                <p className="text-[14px] text-[#6B7280]">{t.noRolesHint}</p>
              </div>
            )}

            {!jobsLoading && !jobsError && !hasSearched && (
              <div className="text-center py-16">
                <p className="text-[15px] text-[#6B7280]">Search for a role or location to see jobs.</p>
              </div>
            )}

            {!jobsLoading && !jobsError && pagedJobs.map(job => (
              <JobRow key={job.id} job={job} saved={saved.has(job.id)} onSave={handleSave} onApply={handleApply} onJobClick={handleJobClick} t={t} />
            ))}

            {!jobsLoading && !jobsError && totalPages > 1 && (
              <div className="flex items-center justify-center gap-1.5 pt-8 pb-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-9 h-9 rounded-full flex items-center justify-center border border-[#D9D9E4] text-[#3C3F4A] hover:border-[#3C3F4A] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronDown className="w-4 h-4 rotate-90" />
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((item, idx) =>
                    item === '…' ? (
                      <span key={`ellipsis-${idx}`} className="w-9 h-9 flex items-center justify-center text-[13px] text-[#6B7280]">…</span>
                    ) : (
                      <button
                        key={item}
                        onClick={() => setCurrentPage(item)}
                        className={`w-9 h-9 rounded-full text-[13.5px] font-semibold transition-all
                          ${currentPage === item
                            ? 'bg-[#12131A] text-white'
                            : 'border border-[#D9D9E4] text-[#3C3F4A] hover:border-[#3C3F4A]'
                          }`}
                      >
                        {item}
                      </button>
                    )
                  )
                }

                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="w-9 h-9 rounded-full flex items-center justify-center border border-[#D9D9E4] text-[#3C3F4A] hover:border-[#3C3F4A] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronDown className="w-4 h-4 -rotate-90" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── BLUE COLLAR ────────────────────────────────────────── */}
        {activeTab === 'bc' && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#F1F0F8] flex items-center justify-center mb-5">
              <Users className="w-8 h-8 text-brand-purple" />
            </div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-100 text-amber-700 text-[11px] font-black uppercase tracking-wider mb-3">
              {t.comingSoon}
            </span>
            <h2 className="font-display font-black text-[22px] sm:text-[26px] text-[#12131A] tracking-tight mb-2">
              {t.bcTitle}
            </h2>
            <p className="text-[14px] text-[#6B7280] max-w-xs leading-relaxed">{t.bcDesc}</p>
          </div>
        )}

        {/* ── WALK-IN DRIVES ─────────────────────────────────────── */}
        {activeTab === 'walkin' && <WalkinPanel t={t} />}

      </main>

      <Footer />

      <MobileFilterSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        filterDefs={activeTab === 'bc' ? BC_FILTER_DEFS : ALL_FILTER_DEFS}
        filters={sheetFilters}
        onApply={applySheet}
        t={t}
      />

      <AnimatePresence>
        {showAuthGate && (
          <AuthGateModal
            onClose={() => setShowAuthGate(false)}
            onLogin={() => navigate('/login')}
            onSignup={() => navigate('/signup')}
            t={t}
          />
        )}
      </AnimatePresence>

      {selectedJob && (
        <JobDetailModal
          job={selectedJob}
          onClose={handleCloseDetail}
          t={t}
        />
      )}
    </div>
  );
}
